// Customer notification API client for the marketplace customer embed —
// TD-384 T25's in-app merchant-notification surface (migration 099,
// design §4.4.7), consumed by the NotificationsPanel component (⚠️ AMENDED
// 2026-10-03, account-area redesign Task 6: this note used to cite
// WelcomePanel's Welcome-section mount — Task 4 made Notifications its own
// section and Task 6 deleted that panel; see NotificationsPanel.tsx's
// AMENDED notes for the mount history).
//
// Wire shape mirrors Go model.CustomerNotification exactly (project rule
// §39) — the same mirror the portal client carries at
// portal/api/marketplace.ts (the wire-type home per controller Ruling 4);
// this file is the EMBED-side client over the same routes, because the
// embed authenticates with a JWT Bearer (embedApiFetch) rather than the
// portal's cookie session:
//   handler: internal/marketplace/handler/customer_handler_notifications.go
//     - HandleMyNotificationsList  (GET  /api/v1/marketplace/me/notifications)
//       → {"data": [...], "unread": N} — the `unread` sibling is why this
//       list goes through embedApiFetchRaw (embedApiFetch's .data unwrap
//       would drop it; re-deriving it client-side would defeat the point
//       of the server-computed field)
//     - HandleMyNotificationMarkRead
//       (POST /api/v1/marketplace/me/notifications/{id}/read) — If-Match
//       REQUIRED (the row's version); §59: re-seed the row from this
//       response
//
// The list is unread-first, newest-first within each read state, with NO
// pagination and NO timestamp on any row: created_at is the server's
// ordering key only (rule #9), so array position IS recency and read_at's
// PRESENCE is the unread signal.

import { embedApiFetch, embedApiFetchRaw } from "../../embed-shared/api-client";

/**
 * The in-app notification kinds the backend CHECK constraint admits today.
 * Deliberately NON-exhaustive on the type level (`| (string & {})`): the
 * surface is first-class shared infrastructure (design §4.4.7) — the next
 * feature that needs to reach a merchant extends `kind`, and a closed TS
 * union here would turn that into an embed compile break. Match by value,
 * fall back gracefully, never switch exhaustively.
 */
export type EmbedCustomerNotificationKind =
  | "referral_redemption_settled"
  | "referral_redemption_revoked"
  | "referral_redemption_adjusted"
  | (string & {});

/**
 * One in-app notification row. Mirrors Go model.CustomerNotification
 * exactly (§39). `payload` is the kind-specific envelope as string-valued
 * keys (amounts are decimal strings); for the settled kind it is EXACTLY
 * the §4.4.7 seven-key envelope the settle email carries — redemption_id,
 * customer_id, mid_number, statement_month, requested_amount,
 * verified_amount, remaining_balance — with BOTH amounts (§30: when they
 * differ the merchant must see the adjustment, never just the final
 * figure).
 *
 * `read_at` is ABSENT while unread — its PRESENCE is the unread signal
 * (the instant is not displayed). `version` is the rule #10 OCC token —
 * mark-read sends it as If-Match; §59: re-seed the row from the mark-read
 * response.
 */
export interface EmbedCustomerNotification {
  id: string; // UUID
  customer_id: number;
  kind: EmbedCustomerNotificationKind;
  payload: Record<string, string>;
  read_at?: string; // RFC3339; ABSENT = unread
  version: number; // rule #10 OCC token — mark-read needs it as If-Match
}

/** Result of listing: the ordered rows plus the unread badge count. */
export interface EmbedNotificationListResult {
  data: EmbedCustomerNotification[];
  unread: number;
}

const NOTIFICATIONS_PATH = "/api/v1/marketplace/me/notifications";

/**
 * List the caller's own notifications, unread-first (newest-first within
 * each read state), with the unread badge count. The count is the
 * SERVER's figure read straight off the envelope — never re-derived from
 * the rows (a divergence between the two is exactly what the server field
 * exists to surface).
 *
 * Throws ApiError(401) when the customer session is gone.
 */
export async function listMyNotifications(): Promise<EmbedNotificationListResult> {
  const res = await embedApiFetchRaw<Partial<EmbedNotificationListResult>>(
    NOTIFICATIONS_PATH,
  );
  return { data: res?.data ?? [], unread: res?.unread ?? 0 };
}

/**
 * Mark one notification read. If-Match REQUIRED (428 absent, 400
 * non-integer, 409 stale — rule #10); NOT idempotency-keyed: the version +
 * read_at state guard IS the idempotency — a re-sent mark-read at the
 * CURRENT version returns the row UNCHANGED (200), so §59 re-seed from
 * this response either way. A foreign or unknown id is an
 * indistinguishable 404 (the IDOR collapse).
 */
export async function markMyNotificationRead(
  id: string,
  version: number,
): Promise<EmbedCustomerNotification> {
  return embedApiFetch<EmbedCustomerNotification>(
    `${NOTIFICATIONS_PATH}/${id}/read`,
    { method: "POST", headers: { "If-Match": String(version) } },
  );
}
